import { useState } from "react";
import { useNavigate } from "react-router";
import { AccountMenu } from "@/components/common/AccountMenu";
import { AppBrand } from "@/components/common/AppBrand";
import { Icon, type IconName } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { NavItemButton } from "@/components/common/NavItemButton";
import { IzanagiMark } from "@/components/common/IzanagiMark";
import { HelpAgentLauncher } from "@/components/help/HelpAgentLauncher";
import { canAccessMainView } from "@/lib/main-view-access";
import { TOP_GAP } from "@/lib/pane-styles";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { MAIN_VIEW_PATHS, useLayoutStore, type MainView } from "@/stores/useLayoutStore";

type NavView = Exclude<MainView, "chats">;

// 折りたたみ時は幅いっぱいに中央寄せするため、展開時のアイコン中心 (左端から 25px) と
// 揃う 50px にしている。48px だと中心が 24px になり、開閉のたびにアイコンが 1px 動く。
const COLLAPSED_WIDTH = 50;
const EXPANDED_WIDTH = 208;

/** 折りたたみ中に「右へ広げられる」ことを示すカーソル。 */
const EXPAND_CURSOR = "e-resize";

/** 展開の当たり判定にする右端の帯の幅。nav の padding 内に収めてボタンと重ねない。 */
const EXPAND_EDGE_WIDTH = 5;

interface NavItem {
	id: NavView;
	icon: IconName;
	label: string;
	/** 選択可能な項目か。false の場合はクリックしても選択状態にならない。 */
	selectable: boolean;
}

const NAV_ITEMS: NavItem[] = [
	{ id: "projects", icon: "agent", label: "エージェント", selectable: true },
	{ id: "volumes", icon: "database", label: "共有ボリューム", selectable: true },
	{ id: "billing", icon: "creditCard", label: "課金", selectable: true },
	{ id: "organization", icon: "user", label: "組織", selectable: true },
	{ id: "devices", icon: "monitor", label: "デバイス", selectable: true },
];

/**
 * メインページ左端のサイドバー。最上部にアプリ名、最下部にヘルプとアカウントメニューを置く。
 * 折りたたみ状態ではアイコンのみを表示し、ホバー時に遅延なしのツールチップで
 * ラベルを示す。マークとボタン以外の余白はどこを押しても展開する。展開状態では
 * アイコンとラベルを並べ、アプリ名の右のボタンで折りたたむ。選択中の項目に
 * 応じてメイン領域の表示内容 (useLayoutStore.mainView) が切り替わる。
 */
export function Sidebar() {
	const navigate = useNavigate();
	// マークと右端の境界線でホバー状態を共有し、どちらを指しても同じ展開アイコンを見せる。
	const [expandHovered, setExpandHovered] = useState(false);
	const collapsed = useLayoutStore((s) => s.sidebarCollapsed);
	const toggleSidebar = useLayoutStore((s) => s.toggleSidebar);
	const selectedId = useLayoutStore((s) => s.mainView);
	const role = useCurrentUserStore((s) => s.role);
	const permissions = useCurrentUserStore((s) => s.permissions);
	const billingManagedInCloud = useCurrentUserStore((s) => s.billingManagedInCloud);
	const deviceManagementAvailable = useCurrentUserStore((s) => s.deviceManagementAvailable);
	const visibleNavItems = NAV_ITEMS.filter((item) =>
		canAccessMainView(item.id, {
			role,
			permissions,
			billingManagedInCloud,
			deviceManagementAvailable,
		}),
	);

	return (
		<nav
			data-testid="sidebar"
			data-collapsed={collapsed}
			style={{
				position: "relative",
				width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
				flexShrink: 0,
				height: "100%",
				display: "flex",
				flexDirection: "column",
				gap: 4,
				// 上端は画面の縁から少し離す。各面が自分の背景色で余白を描く。
				padding: `${TOP_GAP + 8}px 8px 8px`,
				background: "var(--surface-1)",
				borderRight: "var(--pane-header-border)",
				transition: "width 160ms ease",
			}}
		>
			{collapsed ? (
				<CollapsedBrandButton
					onClick={toggleSidebar}
					hovered={expandHovered}
					onHoverChange={setExpandHovered}
				/>
			) : (
				<div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 4 }}>
					<AppBrand style={{ flex: 1, minWidth: 0 }} />
					<span style={{ display: "grid", placeItems: "center", height: 32, flexShrink: 0 }}>
						<IconButton
							icon="panelLeft"
							onClick={toggleSidebar}
							title="サイドバーを折りたたむ"
							data-testid="sidebar-collapse"
						/>
					</span>
				</div>
			)}

			{visibleNavItems.map((item) => (
				<NavItemButton
					key={item.id}
					icon={item.icon}
					label={item.label}
					collapsed={collapsed}
					selected={item.selectable && selectedId === item.id}
					onClick={() => {
						if (item.selectable) navigate(MAIN_VIEW_PATHS[item.id]);
					}}
					testId={`sidebar-item-${item.id}`}
				/>
			))}

			<div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
				<HelpAgentLauncher
					collapsed={collapsed}
					panelLeft={collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}
				/>
				<div style={{ display: "flex", alignItems: "center" }}>
					<AccountMenu iconOnly={collapsed} />
				</div>
			</div>

			{collapsed && (
				<button
					type="button"
					data-testid="sidebar-expand-edge"
					onClick={toggleSidebar}
					onPointerEnter={() => setExpandHovered(true)}
					onPointerLeave={() => setExpandHovered(false)}
					style={{
						position: "absolute",
						top: 0,
						right: 0,
						width: EXPAND_EDGE_WIDTH,
						height: "100%",
						border: 0,
						padding: 0,
						background: "transparent",
						cursor: EXPAND_CURSOR,
					}}
				/>
			)}
		</nav>
	);
}

/** 折りたたみ中のマーク。ホバー (右端の境界線を含む) で展開アイコンに変わり、押すと展開する。 */
function CollapsedBrandButton({
	onClick,
	hovered,
	onHoverChange,
}: {
	onClick: () => void;
	hovered: boolean;
	onHoverChange: (hovered: boolean) => void;
}) {
	return (
		<button
			type="button"
			data-testid="sidebar-expand"
			onClick={onClick}
			onPointerEnter={() => onHoverChange(true)}
			onPointerLeave={() => onHoverChange(false)}
			title="サイドバーを展開"
			data-tooltip-side="right"
			data-tooltip-delay={0}
			style={{
				display: "grid",
				placeItems: "center",
				height: 32,
				flexShrink: 0,
				marginBottom: 4,
				border: 0,
				padding: 0,
				background: "transparent",
				color: "var(--fg-1)",
				cursor: EXPAND_CURSOR,
			}}
		>
			{hovered ? <Icon name="panelLeft" size={18} /> : <IzanagiMark height={18} />}
		</button>
	);
}
